<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';

import Integration from './Integration.vue';
import Spinner from 'shared/components/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import suiteAPI from 'dashboard/api/integrations/suite';

defineProps({
  error: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const store = useStore();
const dialogRef = ref(null);
const redirectDialogRef = ref(null);
const integrationLoaded = ref(false);
const suiteUrl = ref('');
const suiteUrlError = ref('');
const isSubmitting = ref(false);
const redirectSeconds = ref(5);
const pendingRedirectUrl = ref('');
let redirectTimer = null;
const isLoadingStatus = ref(false);
const isLoadingClients = ref(false);
const isConfirming = ref(false);
const suiteStatus = ref({
  connected: false,
  needs_company_selection: false,
  cliente_id: null,
  cliente_nome: null,
  suite_url: null,
});
const clients = ref([]);
const selectedClienteId = ref('');
const selectedClientMeta = ref(null);

const integration = useFunctionGetter('integrations/getIntegration', 'suite');

const integrationAction = computed(() => {
  if (integration.value.enabled) {
    return 'disconnect';
  }
  return 'connect';
});

const showCompanyPicker = computed(
  () => suiteStatus.value.connected && suiteStatus.value.needs_company_selection
);

const connectedCompanyLabel = computed(() => {
  if (
    !suiteStatus.value.connected ||
    suiteStatus.value.needs_company_selection
  ) {
    return '';
  }
  return (
    suiteStatus.value.cliente_nome ||
    (suiteStatus.value.cliente_id
      ? `#${suiteStatus.value.cliente_id}`
      : t('INTEGRATION_SETTINGS.SUITE.COMPANY.UNKNOWN'))
  );
});

const clientOptions = computed(() => {
  const options = clients.value.map(client => ({
    value: Number(client.cliente_id),
    label: client.nome,
  }));
  const selectedId = Number(selectedClienteId.value);
  if (
    selectedId &&
    !options.some(option => option.value === selectedId) &&
    selectedClientMeta.value
  ) {
    options.unshift({
      value: selectedId,
      label: selectedClientMeta.value.nome,
    });
  }
  return options;
});

const openSuiteUrlDialog = () => {
  dialogRef.value?.open();
};

const hideSuiteUrlModal = () => {
  suiteUrl.value = '';
  suiteUrlError.value = '';
  isSubmitting.value = false;
};

const normalizeSuiteUrl = url => {
  const trimmed = String(url || '').trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const validateSuiteUrl = url => {
  try {
    const parsed = new URL(normalizeSuiteUrl(url));
    return ['http:', 'https:'].includes(parsed.protocol) && !!parsed.hostname;
  } catch {
    return false;
  }
};

const clearRedirectTimer = () => {
  if (redirectTimer) {
    clearInterval(redirectTimer);
    redirectTimer = null;
  }
};

const goToSuiteOauth = () => {
  const url = pendingRedirectUrl.value;
  clearRedirectTimer();
  if (url) window.location.href = url;
};

const startRedirectCountdown = async url => {
  pendingRedirectUrl.value = url;
  redirectSeconds.value = 5;
  dialogRef.value?.close();
  await nextTick();
  redirectDialogRef.value?.open();
  clearRedirectTimer();
  redirectTimer = setInterval(() => {
    redirectSeconds.value -= 1;
    if (redirectSeconds.value <= 0) {
      goToSuiteOauth();
    }
  }, 1000);
};

const hideRedirectModal = () => {
  clearRedirectTimer();
  pendingRedirectUrl.value = '';
  redirectSeconds.value = 5;
};

const handleSuiteUrlSubmit = async () => {
  suiteUrlError.value = '';
  const normalizedUrl = normalizeSuiteUrl(suiteUrl.value);
  if (!validateSuiteUrl(normalizedUrl)) {
    suiteUrlError.value = t('INTEGRATION_SETTINGS.SUITE.SUITE_URL.ERROR');
    return;
  }

  isSubmitting.value = true;
  try {
    const { data } = await suiteAPI.connect({ suiteUrl: normalizedUrl });
    if (data.redirect_url) {
      startRedirectCountdown(data.redirect_url);
    }
  } catch (error) {
    suiteUrlError.value =
      error?.response?.data?.error ||
      t('INTEGRATION_SETTINGS.SUITE.SUITE_URL.ERROR');
  } finally {
    isSubmitting.value = false;
  }
};

const loadClients = async (nome = '') => {
  isLoadingClients.value = true;
  try {
    const { data } = await suiteAPI.clients({ nome: nome || undefined });
    clients.value = data.clients || [];
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INTEGRATION_SETTINGS.SUITE.COMPANY.LOAD_ERROR')
    );
  } finally {
    isLoadingClients.value = false;
  }
};

const searchClients = debounce(query => {
  loadClients(query);
}, 300);

const loadStatus = async () => {
  if (!integration.value?.enabled) {
    suiteStatus.value = {
      connected: false,
      needs_company_selection: false,
      cliente_id: null,
      cliente_nome: null,
      suite_url: null,
    };
    return;
  }

  isLoadingStatus.value = true;
  try {
    const { data } = await suiteAPI.status();
    suiteStatus.value = data;
    if (data.needs_company_selection) {
      await loadClients();
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INTEGRATION_SETTINGS.SUITE.STATUS_ERROR')
    );
  } finally {
    isLoadingStatus.value = false;
  }
};

const confirmCompany = async () => {
  if (!selectedClienteId.value) return;

  const selected =
    clients.value.find(
      client => Number(client.cliente_id) === Number(selectedClienteId.value)
    ) || selectedClientMeta.value;

  isConfirming.value = true;
  try {
    const { data } = await suiteAPI.confirm({
      clienteId: selectedClienteId.value,
      clienteNome: selected?.nome,
    });
    suiteStatus.value = data;
    useAlert(t('INTEGRATION_SETTINGS.SUITE.COMPANY.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INTEGRATION_SETTINGS.SUITE.COMPANY.CONFIRM_ERROR')
    );
  } finally {
    isConfirming.value = false;
  }
};

const initializeSuiteIntegration = async () => {
  await store.dispatch('integrations/get', 'suite');
  integrationLoaded.value = true;
  await loadStatus();
};

watch(selectedClienteId, id => {
  if (!id) {
    selectedClientMeta.value = null;
    return;
  }
  const found = clients.value.find(
    client => Number(client.cliente_id) === Number(id)
  );
  if (found) {
    selectedClientMeta.value = found;
  }
});

onMounted(() => {
  initializeSuiteIntegration();
});

onUnmounted(() => {
  clearRedirectTimer();
});
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto max-w-6xl mx-auto">
    <div v-if="integrationLoaded" class="flex flex-col gap-6">
      <Integration
        :integration-id="integration.id"
        :integration-logo="integration.logo"
        :integration-name="integration.name"
        :integration-description="integration.description"
        :integration-enabled="integration.enabled"
        :integration-action="integrationAction"
        :delete-confirmation-text="{
          title: $t('INTEGRATION_SETTINGS.SUITE.DELETE.TITLE'),
          message: $t('INTEGRATION_SETTINGS.SUITE.DELETE.MESSAGE'),
        }"
      >
        <template #action>
          <Button
            teal
            :label="$t('INTEGRATION_SETTINGS.CONNECT.BUTTON_TEXT')"
            @click="openSuiteUrlDialog"
          />
        </template>
      </Integration>

      <div
        v-if="error"
        class="flex items-center justify-center outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-6"
      >
        <p class="text-n-ruby-9">
          {{ $t('INTEGRATION_SETTINGS.SUITE.ERROR') }}
        </p>
      </div>

      <div
        v-if="integration.enabled && !isLoadingStatus"
        class="outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow p-6 flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <p class="text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.SUITE.CONNECTED_URL') }}
          </p>
          <p class="text-n-slate-12 font-medium">
            {{ suiteStatus.suite_url }}
          </p>
        </div>

        <div v-if="!showCompanyPicker" class="flex flex-col gap-1">
          <p class="text-sm text-n-slate-11">
            {{ $t('INTEGRATION_SETTINGS.SUITE.COMPANY.LABEL') }}
          </p>
          <p class="text-n-slate-12 font-medium">
            {{ connectedCompanyLabel }}
          </p>
        </div>

        <div v-if="showCompanyPicker" class="flex flex-col gap-4">
          <p class="text-n-slate-12 font-medium">
            {{ $t('INTEGRATION_SETTINGS.SUITE.COMPANY.SELECT_TITLE') }}
          </p>
          <div
            v-if="isLoadingClients && !clientOptions.length"
            class="flex justify-center py-4"
          >
            <Spinner size="" color-scheme="primary" />
          </div>
          <ComboBox
            v-else
            v-model="selectedClienteId"
            appearance="form"
            use-api-results
            :options="clientOptions"
            :placeholder="$t('INTEGRATION_SETTINGS.SUITE.COMPANY.PLACEHOLDER')"
            :search-placeholder="
              $t('INTEGRATION_SETTINGS.SUITE.COMPANY.SEARCH')
            "
            :empty-state="$t('INTEGRATION_SETTINGS.SUITE.COMPANY.EMPTY')"
            @search="searchClients"
          />
          <div class="flex justify-end">
            <Button
              teal
              :label="$t('INTEGRATION_SETTINGS.SUITE.COMPANY.CONFIRM')"
              :disabled="!selectedClienteId || isConfirming"
              :is-loading="isConfirming"
              @click="confirmCompany"
            />
          </div>
        </div>
      </div>

      <Dialog
        ref="dialogRef"
        :title="$t('INTEGRATION_SETTINGS.SUITE.SUITE_URL.TITLE')"
        :is-loading="isSubmitting"
        @confirm="handleSuiteUrlSubmit"
        @close="hideSuiteUrlModal"
      >
        <Input
          v-model="suiteUrl"
          :label="$t('INTEGRATION_SETTINGS.SUITE.SUITE_URL.LABEL')"
          :placeholder="$t('INTEGRATION_SETTINGS.SUITE.SUITE_URL.PLACEHOLDER')"
          :message="
            !suiteUrlError
              ? $t('INTEGRATION_SETTINGS.SUITE.SUITE_URL.HELP')
              : suiteUrlError
          "
          :message-type="suiteUrlError ? 'error' : 'info'"
        />
      </Dialog>

      <Dialog
        ref="redirectDialogRef"
        :title="$t('INTEGRATION_SETTINGS.SUITE.REDIRECT.TITLE')"
        :description="
          $t('INTEGRATION_SETTINGS.SUITE.REDIRECT.MESSAGE', {
            seconds: redirectSeconds,
          })
        "
        :confirm-button-label="$t('INTEGRATION_SETTINGS.SUITE.REDIRECT.CONFIRM')"
        :show-cancel-button="false"
        @confirm="goToSuiteOauth"
        @close="hideRedirectModal"
      />
    </div>

    <div v-else class="flex items-center justify-center flex-1">
      <Spinner size="" color-scheme="primary" />
    </div>
  </div>
</template>
